import { Injectable, inject } from '@angular/core';
import { Clipboard } from '@capacitor/clipboard';
import { PlatformService } from '../platform';

/**
 * @description
 * Copies text to the clipboard of the current platform: the native clipboard through the
 * Capacitor plugin in the hybrid apps and the browser APIs in the web ones.
 */
@Injectable({
  providedIn: 'root',
})
export class ClipboardService {
  private readonly platform = inject(PlatformService);

  /**
   * @returns {Promise<boolean>} Whether the text could be copied. It never throws: not being able
   * to copy is an expected outcome that the caller may want to report to the user.
   */
  public async writeText(text: string): Promise<boolean> {
    if (!text?.trim()) return false;

    return this.platform.isHybrid ? this.writeWithNativeClipboard(text) : this.writeWithBrowserClipboard(text);
  }

  private async writeWithNativeClipboard(text: string): Promise<boolean> {
    try {
      await Clipboard.write({ string: text });
      return true;
    } catch (error) {
      console.error('Error copying text with the native clipboard:', error);
      return false;
    }
  }

  private async writeWithBrowserClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        console.error('Error copying text with the Clipboard API, trying the legacy API:', error);
      }
    }

    return this.writeWithLegacyBrowserClipboard(text);
  }

  private writeWithLegacyBrowserClipboard(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);

    try {
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      return document.execCommand('copy');
    } catch (error) {
      console.error('Error copying text with the legacy Clipboard API:', error);
      return false;
    } finally {
      textarea.remove();
    }
  }
}
